// src/components/Home Dashboard/RecentItems.jsx
import React, { useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  Stack,
  IconButton,
} from "@mui/material";

import BusinessIcon from "@mui/icons-material/Business";
import ContactsIcon from "@mui/icons-material/Contacts";
import WorkHistoryIcon from "@mui/icons-material/WorkHistory";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import HistoryIcon from "@mui/icons-material/History";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../providers/AuthProvider";
import { getRecentItems } from "./RecentItemsHelper";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";

const CARD_SX = {
  height: "100%",
  border: "1px solid rgba(0,51,108,0.1)",
  borderRadius: "14px",
  boxShadow: "0 1px 4px rgba(15,23,42,0.05), 0 8px 24px rgba(15,23,42,0.07)",
  background: "#ffffff",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "3px",
    background: BRAND_BLUE,
  },
};

const NAV_BTN_SX = {
  width: 26,
  height: 26,
  borderRadius: "6px",
  border: "1px solid rgba(0,51,108,0.15)",
  backgroundColor: "#eef2f8",
  color: BRAND_BLUE,
  transition: "all 0.15s ease",
  "&:hover": { backgroundColor: "#e2eaf5", borderColor: "rgba(0,51,108,0.35)" },
  "&.Mui-disabled": { backgroundColor: "transparent", borderColor: "rgba(0,51,108,0.07)", color: "#b0bdd0" },
};

export default function RecentItems() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const {
    companies = [],
    contacts = [],
    projects = [],
    prospects = [],
    prospecting_contacts = [],
  } = useMemo(() => {
    if (!user?.id) return {};
    return getRecentItems(user.id) || {};
  }, [user?.id]);

  const slides = [];
  if (companies.length > 0)
    slides.push({ key: "companies", label: "BR&E Companies", items: companies, type: "companies" });
  if (contacts.length > 0)
    slides.push({ key: "contacts", label: "BR&E Contacts", items: contacts, type: "contacts" });
  if (prospects.length > 0)
    slides.push({ key: "prospects", label: "Prospects", items: prospects, type: "prospects" });
  if (prospecting_contacts.length > 0)
    slides.push({ key: "prospecting-contacts", label: "Prospect Contacts", items: prospecting_contacts, type: "prospecting_contacts" });
  if (projects.length > 0)
    slides.push({ key: "projects", label: "Projects", items: projects, type: "projects" });

  const hasAny = slides.length > 0;
  const [activeIndex, setActiveIndex] = useState(0);
  const currentSlide = slides[activeIndex] || null;

  const handlePrev = () => setActiveIndex((i) => Math.max(0, i - 1));
  const handleNext = () => setActiveIndex((i) => Math.min(slides.length - 1, i + 1));

  return (
    <Card sx={CARD_SX}>
      <CardContent sx={{ p: 2.5, pt: 2.75, flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>

        {/* ===== Header ===== */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: "10px",
                background: BRAND_GREEN,
                color: "#fff",
                display: "grid",
                placeItems: "center",
                flex: "0 0 auto",
                boxShadow: `0 2px 8px ${BRAND_GREEN}40`,
              }}
            >
              <HistoryIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: BRAND_BLUE, fontSize: 16.5, fontWeight: 700, lineHeight: 1.15 }}>
                My Recent Activity
              </Typography>
              <Typography sx={{ color: "#3a5c08", fontSize: 12.5, mt: 0.2, fontFamily: "'DM Sans', sans-serif", fontWeight: 500, letterSpacing: 0.3 }}>
                Recently viewed records
              </Typography>
            </Box>
          </Box>

          {hasAny && currentSlide && (
            <Stack direction="row" spacing={1} alignItems="center">
              {/* Category pill */}
              <Box
                sx={{
                  px: 1.25,
                  py: 0.4,
                  borderRadius: "20px",
                  backgroundColor: BRAND_GREEN,
                  flexShrink: 0,
                }}
              >
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "#ffffff", letterSpacing: 0.8, textTransform: "uppercase", whiteSpace: "nowrap" }}>
                  {currentSlide.label}
                </Typography>
              </Box>

              {/* Nav arrows */}
              <Stack direction="row" spacing={0.5}>
                <IconButton size="small" onClick={handlePrev} disabled={activeIndex === 0} sx={NAV_BTN_SX}>
                  <ArrowBackIosNewIcon sx={{ fontSize: 12 }} />
                </IconButton>
                <IconButton size="small" onClick={handleNext} disabled={activeIndex === slides.length - 1} sx={NAV_BTN_SX}>
                  <ArrowForwardIosIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </Stack>
            </Stack>
          )}
        </Box>

        <Divider sx={{ mb: 1, borderColor: "rgba(0,51,108,0.07)" }} />

        {/* ===== Empty State ===== */}
        {!hasAny && (
          <Box
            sx={{
              flex: 1,
              borderRadius: "10px",
              border: "1px dashed rgba(0,51,108,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 3,
              textAlign: "center",
            }}
          >
            <Typography sx={{ color: "#8a98b3", fontStyle: "italic", fontSize: 14 }}>
              You haven&apos;t viewed any companies, contacts, projects, or prospects yet.
            </Typography>
          </Box>
        )}

        {/* ===== Scrollable List ===== */}
        {hasAny && currentSlide && (
          <Box sx={{ flex: 1, overflowY: "auto", pr: 0.5, minHeight: 0 }}>
            <List key={currentSlide.key} dense disablePadding>
              {currentSlide.items.map((item) => {
                const isCompany = currentSlide.type === "companies";
                const isContact = currentSlide.type === "contacts";
                const isProspect = currentSlide.type === "prospects";
                const isProspectingContact = currentSlide.type === "prospecting_contacts";

                const route = isCompany
                  ? `/bre-companies/${item.id}`
                  : isContact
                    ? `/bre-contacts/${item.id}`
                    : isProspectingContact
                      ? `/prospecting-contacts/${item.id}`
                      : isProspect
                        ? `/prospecting-companies/${item.id}`
                        : `/projects/${item.id}`;

                const Icon = isCompany
                  ? BusinessIcon
                  : isContact
                    ? ContactsIcon
                    : isProspect
                      ? PersonSearchIcon
                      : WorkHistoryIcon;

                return (
                  <Tooltip key={`${currentSlide.type}-${item.id}`} title={item.name} arrow placement="left">
                    <ListItemButton
                      onClick={() => navigate(route)}
                      sx={{
                        borderRadius: "8px",
                        px: 1,
                        py: 0.6,
                        mb: 0.25,
                        borderLeft: "3px solid transparent",
                        transition: "all 0.15s ease",
                        "&:hover": {
                          backgroundColor: "#f4f7fc",
                          borderLeftColor: BRAND_GREEN,
                          "& .row-arrow": { opacity: 1, transform: "translateX(2px)" },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 38 }}>
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: "7px",
                            backgroundColor: `${BRAND_GREEN}18`,
                            display: "grid",
                            placeItems: "center",
                          }}
                        >
                          <Icon sx={{ fontSize: 23, color: BRAND_GREEN }} />
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography noWrap sx={{ fontWeight: 600, fontSize: 14.5, color: BRAND_BLUE }}>
                            {item.name}
                          </Typography>
                        }
                      />
                      <ArrowForwardIosIcon
                        className="row-arrow"
                        sx={{ fontSize: 11, color: "#b0bdd0", opacity: 0.5, transition: "all 0.15s ease", flexShrink: 0 }}
                      />
                    </ListItemButton>
                  </Tooltip>
                );
              })}
            </List>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
